<template>
    <div>
        <Header ref="header" />
        <router-view />
        <Footer />
        <alert-dialog ref="alertDialog"></alert-dialog>
    </div>
</template>

<style lang="scss">
    #app {
        font-family: var(--bs-font-sans-serif);
        color: var(--bs-body-color);
        background-color: var(--bs-body-bg);
    }
</style>

<script lang="ts">

import { defineComponent } from "vue";

import { SignalR } from "./support/SignalR";
import Tooltip from "@/shared/support/Tooltip";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";
import AlertDialog, { confirmYesNo, initializeAlertDialog } from "@/shared/components/common/AlertDialog.vue";
import { Global } from "./support/GlobalData";
import { CalibrationEventSupport } from "./support/CalibrationEvent";
import { CalibrationEvent } from "@/shared/enums/CalibrationEvent";
import { DataRequest } from "@/shared/support/Data";
import Globals from "./support/Globals";
import TemperatureInfoDto from "./shared/models/TemperatureInfoDto";
import Toast from "./shared/support/Toast";

export function getMainApp(): any {
    return Global.MainApp;
}

export default defineComponent({
    components: {
        Header,
        Footer,
        AlertDialog
    },
    created(): void {
        Global.MainApp = this;
    },
    mounted(): void {
        initializeAlertDialog(this.$refs.alertDialog as any);
        Tooltip.initializeTooltips();

        // Init signalr
        SignalR.start(Globals.GetSignalRFunction());

        DataRequest.getWebAPI = (): string => {
            return Globals.GetWebApi();
        };
        DataRequest.setRequestHeaders = (request: XMLHttpRequest, useAuthentication: boolean): void => {
            request.setRequestHeader("Companion-Api-Version", "v1.0");
            if (DataRequest.haveToken && DataRequest.getToken && DataRequest.haveToken())
                request.setRequestHeader(import.meta.env.VITE_COMPANION_HEADER!, DataRequest.getToken());
        };
        DataRequest.getToken = (): string => {
            return btoa(`${Global.CalibrationSessionGuid};${Global.CompanionGuid}`);
        };
        DataRequest.haveToken = (): boolean => {
            return !!Global.CalibrationSessionGuid && !!Global.CompanionGuid;
        };
        DataRequest.getMaxRetries = (): number => {
            return 3;
        };

        SignalR.onTemperatureInformation = this.onTemperatureInfo;
    },
    unmounted(): void {
        SignalR.onTemperatureInformation = null;
    },
    methods: {
        onTemperatureInfo(info: TemperatureInfoDto): void {
            if (!info.WarningMessage) return;
            switch (info.WarningLevel) {
                case 1: 
                    Toast.success(info.WarningMessage);
                    break;
                case 2: 
                    Toast.warning(info.WarningMessage);
                    break;
                case 3: 
                    Toast.error(info.WarningMessage);
                    break;
            }
        },

        // Session Management

        connectSession(action: () => void): void {
            const q = (this as any).$router.currentRoute.value.query;
            const connection = q.c as string;
            const calibrationSessionGuid = q.s as string;
            if (!connection || !calibrationSessionGuid) {
                Global.MainApp.routerPush("/NoConnection");
                return;
            }
            Global.CompanionGuid = connection;
            Global.CalibrationSessionGuid = calibrationSessionGuid;

            if (!SignalR.isCompanionStarted()) {
                this.startCompanionSession(action);
            } else {
                action();
            }
        },

        startCompanionSession(action: () => void): void {
            // wait for signalR and companion session to start
            setTimeout((): void => {
                if (!SignalR.startCompanionSession()) {
                    this.startCompanionSession(action);
                } else {
                    action();
                }
            }, 100);
        },

        // Main App accessors

        updateHeader(): void {
            (this.$refs.header as any).update();
        },

        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        routerPush(url: string, query?: any): void {
            let complete = `${url}?c=${Global.CompanionGuid}&s=${Global.CalibrationSessionGuid}&x=${this.ticks()}`;

            if (query) {
                let args = "";
                for (const key in query) {
                    args += "&";
                    const val = query[key];
                    if (val !== undefined && val !== null)
                        args += `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
                    else
                        args += `${encodeURIComponent(key)}=`;
                }
                complete += args;
            }
            (this as any).$router.replace(complete);
        },
        ticks(): string {
            const dNow = new Date();
            const dStart = new Date(1970, 0, 1);
            const seconds = dNow.getTime();
            const secondsStart = dStart.getTime();
            const dateDifference = seconds - secondsStart;
            return dateDifference.toString();
        },

        cancelCalibration(): void {
            confirmYesNo("Are you sure you want to cancel this calibration?")
                .then((clicked: ButtonClicked) => {
                    if (clicked === ButtonClicked.Ok) {
                        CalibrationEventSupport.sendEvent(CalibrationEvent.CalibrationCompanionCanceled);
                        Global.MainApp.routerPush("/Canceled");
                    }
                });
        },
    },
});

</script>
